import analyticDataStore, {
  IBaseAnalyticsCombined,
} from "@/store/analyticsDataStore";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import DateRangeAndSlider from "../ClientSummaryReport/DateRangeAndSlider";
import CurrencyAndStructureFilters from "./CurrencyAndStructureFilters";
import CounterpartyDonut from "./CounterpartyDonut";
import CounterpartyMainContent from "./CounterpartyMainContent";
import {
  filterByCurrencyAll,
  filterByDatesTimeSent,
  filterByStructureAll,
} from "../analyticsHelperFunctions";
import { orderMapDV01Filters } from "../BrokerRank/CurrencyAndDV01Filters";

export interface ICounterpartyReport {
  counterparty: string;
  trades: number;
  // tradesHit: number; // Need AXE data.
  // hitRate: number; // Need AXE data.
  tradesWon: number;
  winRate: number;
  notionalSeen: number;
  notionalWon: number;
  DV01Seen: number;
  DV01Won: number;
  averageSpread: number;
  sizeShown: number;
}

export interface ICounterpartyReportFilters {
  range: number[];
  selectedCurrencies: string[];
  selectedStructure: string[];
}

export const baseFilterCounterpartyReport: ICounterpartyReportFilters = {
  range: [],
  selectedCurrencies: [],
  selectedStructure: [],
};

function convertDataForCounterpartyReport(
  data: IBaseAnalyticsCombined[],
  dataFilters: ICounterpartyReportFilters,
): IBaseAnalyticsCombined[] {
  // initial data filter
  const filteredByDates = filterByDatesTimeSent(data, dataFilters);
  const currencyFiltered = filterByCurrencyAll(filteredByDates, dataFilters);
  const structureFiltered = filterByStructureAll(currencyFiltered, dataFilters);

  const filteredData = structureFiltered;
  const streams = []; // Push data here and return as the arr for all visuals to work from.

  const counterpartyReportDataFiltered = filteredData.reduce<
    Record<string, object>
  >((acc: any, dataItem: IBaseAnalyticsCombined) => {
    const counterparty = dataItem.RPCustomer;

    if (!acc[counterparty]) {
      acc[counterparty] = [];
    }
    acc[counterparty].push(dataItem);
    return acc;
  }, {});

  for (const [key, value] of Object.entries(counterpartyReportDataFiltered)) {
    const uniqueTradesSeen: string[] = [];
    const tradesWon: string[] = [];
    let increaseTradesWon = false;
    const objs = value as IBaseAnalyticsCombined[];
    const reducedCp = objs.reduce(
      (acc: any, obj: IBaseAnalyticsCombined) => {
        let increaseTrades = false;
        if (!uniqueTradesSeen.includes(obj.tradeID)) {
          uniqueTradesSeen.push(obj.tradeID);
          increaseTrades = true;
        }
        if (increaseTrades) {
          acc.trades = acc.trades + 1;
        }
        acc.notionalSeen = acc.notionalSeen + Number(obj.notional);
        acc.DV01Seen = acc.DV01Seen + Math.abs(Number(obj.DV01));
        if (obj.lastAction == "Affirmed") {
          if (!tradesWon.includes(obj.tradeID)) {
            tradesWon.push(obj.tradeID);
            increaseTradesWon = true;
          }

          if (increaseTradesWon) {
            acc.tradesWon = acc.tradesWon + 1;
          }
          acc.blocksWonTally = acc.blocksWonTally + 1;
          acc.spreadTotal = acc.spreadTotal + Math.abs(Number(obj.spreadBPS));
          acc.notionalWon = acc.notionalWon + Number(obj.notional);
          acc.DV01Won = acc.DV01Won + Math.abs(Number(obj.DV01));
        }

        if (
          acc.DV01Buckets.find(
            (item: {
              index: number; // based off orderMapDV01Filters
              bucket: string;
              trades: number;
              tradesWon: number;
              blocksWonTally: number;
              winRate: number;
              notionalSeen: number;
              notionalWon: number;
              DV01Seen: number;
              DV01Won: number;
              spreadTotal: number;
              sizeShown: number;
            }) => item.bucket == obj.DV01Buckets,
          )
        ) {
          acc.DV01Buckets = acc.DV01Buckets.map((bucketGroup: any) => {
            if (bucketGroup.bucket != obj.DV01Buckets) {
              return bucketGroup;
            } else {
              return {
                ...bucketGroup,
                // index:
                //   orderMapDV01Filters[obj.DV01Buckets] ??
                //   Number.MAX_SAFE_INTEGER,
                trades: increaseTrades
                  ? bucketGroup.trades + 1
                  : bucketGroup.trades,
                tradesWon: increaseTradesWon
                  ? bucketGroup.tradesWon
                  : bucketGroup.trades,
                blocksWonTally: bucketGroup.blocksWonTally + 1,
                spreadTotal:
                  bucketGroup.spreadTotal + Math.abs(Number(obj.spreadBPS)),
                notionalSeen: bucketGroup.notionalSeen + Number(obj.notional),
                DV01Seen: bucketGroup.DV01Seen + Math.abs(Number(obj.DV01)),
                notionalWon:
                  obj.lastAction == "Affirmed"
                    ? bucketGroup.notionalWon + Number(obj.notional)
                    : bucketGroup.notional,
                DV01Won:
                  obj.lastAction == "Affirmed"
                    ? bucketGroup.DV01Won + Math.abs(Number(obj.DV01))
                    : bucketGroup.DV01Won,
              };
            }
          });
        } else {
          acc.DV01Buckets = [
            ...acc.DV01Buckets,
            {
              index:
                orderMapDV01Filters[obj.DV01Buckets] ?? Number.MAX_SAFE_INTEGER,
              bucket: obj.DV01Buckets,
              trades: 1,
              tradesWon: increaseTradesWon ? 1 : 0,
              blocksWonTally: 1,
              spreadTotal: Math.abs(Number(obj.spreadBPS)),
              notionalSeen: Number(obj.notional),
              DV01Seen: Math.abs(Number(obj.DV01)),
              notionalWon:
                obj.lastAction == "Affirmed" ? Number(obj.notional) : 0,
              DV01Won:
                obj.lastAction == "Affirmed" ? Math.abs(Number(obj.DV01)) : 0,
            },
          ];
        }

        increaseTrades = false;
        increaseTradesWon = false;

        return acc;
      },
      {
        counterparty: key,
        trades: 0,
        tradesWon: 0,
        blocksWonTally: 0,
        winRate: 0,
        notionalSeen: 0,
        notionalWon: 0,
        DV01Seen: 0,
        DV01Won: 0,
        spreadTotal: 0,
        sizeShown: 0,
        DV01Buckets: [],
      },
    );
    streams.push(reducedCp);
  }
  return streams;
}

const CounterpartyReportIndex = () => {
  const { theme } = useTheme();
  const analyticsZustand = analyticDataStore((state) => state.analyticData);

  const [data, setData] = useState<IBaseAnalyticsCombined[]>(analyticsZustand);

  useEffect(() => {
    setData(analyticsZustand);
  }, [analyticsZustand]);

  const [dataFilters, setDataFilters] = useState<ICounterpartyReportFilters>(
    baseFilterCounterpartyReport,
  );

  const [filteredData, setFilteredData] = useState<IBaseAnalyticsCombined[]>(
    convertDataForCounterpartyReport(data, dataFilters),
  );
  useEffect(() => {
    setFilteredData(convertDataForCounterpartyReport(data, dataFilters));
  }, [data, dataFilters]);

  return (
    <div data-testid="CounterpartyReportIndex" className="h-full w-full p-1">
      <div className="flex h-full w-full gap-3">
        <div className="flex h-full w-[230px] min-w-[230px] flex-col gap-3">
          {/* TOP SQUARE - Split into 2 (top half)*/}
          <div
            className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} h-1/2 w-full p-2`}
          >
            <div className="h-full w-full">
              <div
                data-testid="DateRangeAndSliderContainer"
                className="flex h-full w-full flex-col "
              >
                <DateRangeAndSlider
                  dataFilters={dataFilters}
                  setDataFilters={setDataFilters}
                  isSecondary={false}
                />
                {/* Filter toggles */}
                <CurrencyAndStructureFilters
                  data={data}
                  setData={setData}
                  dataFilters={dataFilters}
                  setDataFilters={setDataFilters}
                />
              </div>
            </div>
          </div>
          <div
            className={`${theme == "dark" ? "agChartCubeBorderDark" : "agChartCubeBorder"} h-1/2 w-[calc(100%+10rem)] p-2`}
          >
            <CounterpartyDonut filteredData={filteredData} />
          </div>
        </div>
        {/* Responsive section of widget frame - will expand and contract as requierd. */}
        {/* Main chart */}
        <CounterpartyMainContent filteredData={filteredData} />
      </div>
    </div>
  );
};

export default CounterpartyReportIndex;
